import React, { useEffect, useState, useRef } from "react";
import { MapPin, Phone, Mail, Globe, Facebook } from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./PageCarteVelo.css";

import { atelierIcon, magasinIcon, magasinIconOrange } from "./Icones";

const API_URL = process.env.REACT_APP_API_URL;
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// 🔴 Icône goutte rouge pour l'utilisateur
const userIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -35],
});

// 🔹 Hook pour recentrer la carte sur l'utilisateur
const CenterOnUser = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 13, { animate: true, duration: 1.5 });
    }
  }, [position, map]);
  return null;
};

/* 🔹 Hook pour surveiller le zoom */
const ZoomWatcher = ({ setZoom }) => {
  const map = useMap();

  useEffect(() => {
    const updateZoom = () => setZoom(map.getZoom());
    updateZoom();
    map.on("zoomend", updateZoom);

    return () => map.off("zoomend", updateZoom);
  }, [map, setZoom]);
  return null;
};

const PageCarteVelos = () => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtre, setFiltre] = useState("tous");
  const [userPosition, setUserPosition] = useState(null);

  const user = useSelector((state) => state.user);
  const [editingData, setEditingData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [magasinSelectionne, setMagasinSelectionne] = useState("");
  const [zoom, setZoom] = useState(5);

  const mapRef = useRef(null);
  const markerRefs = useRef({});

  const [newLieu, setNewLieu] = useState({
    type: "Atelier communautaire",
    nom: "",
    adresse: "",
    ville: "",
    postal: "",
    site_web: "",
    facebook: "",
    telephone: "",
    courriel: "",
    latitude: "",
    longitude: "",
  });

  const FIELD_PLACEHOLDERS = {
    nom: "Nom du lieu",
    adresse: "Adresse",
    ville: "Ville",
    postal: "Code postal",
    telephone: "Téléphone (ex: 418 555-1234)",
    site_web: "Site web (ex: https://example.org)",
    courriel: "Courriel (ex: info@example.org)",
    facebook: "Page Facebook (ex: https://facebook.com/nomdelapage)",
  };

  // 🔹 Géolocalisation utilisateur
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPosition([pos.coords.latitude, pos.coords.longitude]),
      (err) => console.warn("Géolocalisation refusée", err),
      { enableHighAccuracy: true }
    );
  }, []);

  // Charger les points
  useEffect(() => {
    const chargerCarte = async () => {
      try {
        const res = await axiosInstance.get("/api/carte");
        setPoints(res.data.data || []);
      } catch (err) {
        console.error(err);
        setError("Impossible de charger la carte");
      } finally {
        setLoading(false);
      }
    };
    chargerCarte();
  }, []);

  // 🔄 Recharger les données d'origine
  const reloadCarte = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/api/carte/carte");
      const ajoutes = res.data?.ajoutes ?? 0;
      if (ajoutes > 0) {
        alert(`✅ ${ajoutes} nouveaux lieux ont été ajoutés à la carte.`);
      } else {
        alert("ℹ️ Aucun nouveau lieu à ajouter.");
      }
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Impossible de recharger les données d'origine");
      alert("❌ Erreur lors du rechargement des données.");
    } finally {
      setLoading(false);
    }
  };

  const pointsFiltres = points.filter((p) => {
    if (filtre === "tous") return true;
    const type = p.type?.toLowerCase() || "";
    if (filtre === "atelier") return type.includes("atelier");
    if (filtre === "reparation") return type.includes("réparation");
    if (filtre === "magasin") return type.includes("magasin");
    return true;
  });

  // 🔹 Magasins triés A → Z (pour le dropdown admin)
  const magasinsTries = points
    .filter((p) => p.type?.toLowerCase().includes("magasin"))
    .sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));

  // 🔹 Fonctions ajout / modification / suppression
  const handleChange = (id, e) => {
    const { name, value } = e.target;
    setEditingData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));
  };

  const handleSave = async (id) => {
    try {
      const data = editingData[id];
      if (!data) return;
      await axiosInstance.put(`/api/carte/${id}`, data);
      setPoints((prev) => prev.map((p) => (p._id === id ? { ...p, ...data } : p)));
      alert("Modifications enregistrées !");
    } catch (err) {
      console.error("Erreur sauvegarde :", err);
      alert("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce lieu ?")) return;
    try {
      await axiosInstance.delete(`/api/carte/${id}`);
      setPoints((prev) => prev.filter((p) => p._id !== id));
      alert("Lieu supprimé !");
    } catch (err) {
      console.error("Erreur lors de la suppression :", err);
      alert("Erreur lors de la suppression");
    }
  };

  const handleNewChange = (e) => {
    const { name, value } = e.target;
    setNewLieu((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddLieu = async () => {
    try {
      if (!newLieu.latitude || !newLieu.longitude) {
        alert("Latitude et longitude requises");
        return;
      }
      const res = await axiosInstance.post("/api/carte", newLieu);
      setPoints((prev) => [...prev, res.data.data]);

      // 🔹 Réinitialiser le formulaire
      setNewLieu({
        type: "Atelier communautaire",
        nom: "",
        adresse: "",
        ville: "",
        postal: "",
        site_web: "",
        facebook: "",
        telephone: "",
        courriel: "",
        latitude: "",
        longitude: "",
      });

      setShowAddForm(false); // fermer le formulaire
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l’ajout");
    }
  };

  if (loading) return <p className="text-center mt-8">Chargement de la carte…</p>;
  if (error) return <p className="text-center mt-8 text-red-600">{error}</p>;

  return (
    <div className="relative w-screen h-screen">
      <MapContainer
        ref={mapRef}
        center={[52.0, -71.5]}
        zoom={5}
        scrollWheelZoom
        className="absolute inset-0 z-0"
      >
        <ZoomWatcher setZoom={setZoom} />
        <TileLayer
          attribution="© OpenStreetMap © CartoDB"
          url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
        />
        <TileLayer
          attribution="© OpenStreetMap © CartoDB"
          url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
        />

        {/* 🔹 Marker utilisateur */}
        {userPosition && <CenterOnUser position={userPosition} />}
        {userPosition && (
          <Marker position={userPosition} icon={userIcon}>
            <Popup>Vous êtes ici</Popup>
          </Marker>
        )}

        {/* 🔹 Markers lieux */}
        {pointsFiltres.map((p) =>
          p.latitude && p.longitude ? (
            <Marker
              key={p._id}
              ref={(el) => {
                if (el) markerRefs.current[p._id] = el;
              }}
              position={[p.latitude, p.longitude]}
              icon={
                p.type?.toLowerCase().includes("atelier")
                  ? atelierIcon
                  : p.type?.toLowerCase().includes("magasin")
                  ? magasinIconOrange
                  : magasinIcon
              }
            >
              {zoom >= 14 && p.nom && (
                <Tooltip
                  permanent
                  interactive
                  direction="top"
                  offset={[0, -28]}
                  opacity={1}
                  className="lieu-tooltip"
                >
                  <span
                    className={`lieu-tooltip ${
                      p.type?.toLowerCase().includes("atelier")
                        ? "tooltip-atelier"
                        : p.type?.toLowerCase().includes("réparation")
                        ? "tooltip-reparation"
                        : p.type?.toLowerCase().includes("magasin")
                        ? "tooltip-magasin"
                        : ""
                    }`}
                  >
                    {p.nom}
                  </span>
                </Tooltip>
              )}

              <Popup>
                {user?.user?.role === "admin" ? (
                  <div className="popup-form space-y-2 w-80">
                    {["nom", "adresse", "ville", "postal", "site_web", "facebook", "telephone", "courriel","latitude","longitude"].map(
                      (field) => (
                        <input
                          key={field}
                          name={field}
                          placeholder={FIELD_PLACEHOLDERS[field]}
                          value={editingData[p._id]?.[field] ?? p[field] ?? ""}
                          onChange={(e) => handleChange(p._id, e)}
                          className="w-full border p-1 rounded"
                        />
                      )
                    )}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleSave(p._id)}
                        className="bg-green-600 text-white px-3 py-1 rounded"
                      >
                        Sauvegarder
                      </button>
                      <button
                        onClick={() => handleDelete(p._id)}
                        className="bg-red-600 text-white px-3 py-1 rounded"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <a
                      href={
                        userPosition
                          ? `https://www.google.com/maps/dir/?api=1&origin=${userPosition[0]},${userPosition[1]}&destination=${encodeURIComponent(
                              `${p.nom || ""} ${p.adresse || ""} ${p.ville || ""} ${p.postal || ""} ${p.description || ""} ${p.latitude || ""},${p.longitude || ""}`
                            )}&travelmode=bicycling`
                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              `${p.nom || ""} ${p.adresse || ""} ${p.ville || ""} ${p.postal || ""} ${p.description || ""} ${p.latitude || ""},${p.longitude || ""}`
                            )}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block space-y-1 hover:opacity-80 transition"
                    >
                      {p.nom && <div className="font-semibold">{p.nom}</div>}

                     {(p.adresse || p.ville || p.postal) && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-8 h-8 text-blue-600 mt-0.5" />
                          <span>
                            {[p.adresse, p.ville, p.postal]
                              .filter(value =>
                                value &&
                                value.trim() !== "" &&
                                value.trim().toLowerCase() !== "none" &&
                                value.trim().toLowerCase() !== "null"
                              )
                              .join(" ")}
                          </span>
                        </div>
                      )}
                    </a>

                    {/* TELEPHONE */}
                    {p.telephone && (
                      <a
                        href={`tel:${p.telephone.replace(/\s+/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-blue-600 underline hover:opacity-80 transition"
                      >
                        <Phone className="w-4 h-4" />
                        {p.telephone}
                      </a>
                    )}

                    {/* COURRIEL */}
                    {p.courriel && (
                      <a
                        href={`mailto:${p.courriel}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-blue-600 underline hover:opacity-80 transition"
                      >
                        <Mail className="w-4 h-4" />
                        {p.courriel}
                      </a>
                    )}

                    {/* SITE WEB */}
                    {p.site_web && (
                      <a
                        href={p.site_web.startsWith("http") ? p.site_web : `https://${p.site_web}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-blue-600 underline hover:opacity-80 transition"
                      >
                        <Globe className="w-4 h-4" />
                        Site
                      </a>
                    )}

                    {/* FACEBOOK */}
                    {p.facebook && (
                      <a
                        href={p.facebook.startsWith("http") ? p.facebook : `https://${p.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-blue-600 underline hover:opacity-80 transition"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                    )}

                    {/* DESCRIPTION */}
                    {p.description && (
                      <div className="pt-1 text-gray-700">
                        {p.description}
                      </div>
                    )}

                  </div>


                )}
              </Popup>
            </Marker>
          ) : null
        )}
      </MapContainer>

      {/* 🔽 Dropdown magasins (admin uniquement) */}
      {user?.user?.role === "admin" && (
        <div className="absolute top-4 right-56 z-10 bg-white/90 backdrop-blur shadow rounded-lg p-2">
          <select
            value={magasinSelectionne}
            onChange={(e) => {
              const selectedId = e.target.value;
              setMagasinSelectionne(selectedId);

              const magasin = points.find((p) => p._id === selectedId);
              if (magasin && magasin.latitude && magasin.longitude) {
                const map = mapRef.current;
                if (map) {
                  map.flyTo([magasin.latitude, magasin.longitude], 16, { animate: true, duration: 1.5 });
                }

                const marker = markerRefs.current[selectedId];
                if (marker) {
                  marker.openPopup();
                }
              }
            }}
            className="border rounded px-3 py-2 text-sm w-64"
          >
            <option value="">📍 Liste des magasins</option>
            {magasinsTries.map((m) => (
              <option key={m._id} value={m._id}>
                {m.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 🔹 Overlay filtres */}
      <div className="filtres-overlay absolute top-4 right-4 z-10 bg-white/90 backdrop-blur shadow rounded-lg p-3 space-y-2">
        <button
          onClick={() => setFiltre("tous")}
          className={`block w-full px-4 py-2 rounded transition-colors duration-200 ${
            filtre === "tous" ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        >
          Tous
        </button>

        <button
          onClick={() => setFiltre("atelier")}
          className={`block w-full px-4 py-2 rounded transition-colors duration-200 ${
            filtre === "atelier" ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        >
          Ateliers communautaires
        </button>

        <button
          onClick={() => setFiltre("reparation")}
          className={`block w-full px-4 py-2 rounded transition-colors duration-200 ${
            filtre === "reparation" ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        >
          Stations réparation de vélo
        </button>

        <button
          onClick={() => setFiltre("magasin")}
          className={`block w-full px-4 py-2 rounded transition-colors duration-200 ${
            filtre === "magasin" ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        >
          Magasins de vélo
        </button>

        <p className="text-xs text-center text-gray-600 pt-1">{pointsFiltres.length} lieux</p>
      </div>

      {/* 🔄 Bouton recharger données d'origine (admin) */}
      {user?.user?.role === "admin" && (
        <button
          onClick={reloadCarte}
          title="Recharger les données d'origine"
          disabled={loading}
          className={`fixed bottom-20 right-6 z-20 w-14 h-14 rounded-full text-2xl shadow-lg transition ${
            loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-500 text-white"
          }`}
        >
          {loading ? <span className="inline-block animate-spin">⟳</span> : <span>⟳</span>}
        </button>
      )}

      {/* 🔹 Bouton + ajout admin */}
      {user?.user?.role === "admin" && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-6 right-6 z-20 bg-green-600 text-white w-14 h-14 rounded-full text-3xl shadow-lg"
        >
          +
        </button>
      )}

      {/* 🔹 Formulaire ajout */}
      {showAddForm && (
        <div className="fixed inset-0 z-30 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl space-y-2">
            <h2 className="text-xl font-bold">Ajouter un lieu</h2>
            <select name="type" value={newLieu.type} onChange={handleNewChange} className="w-full border p-2 rounded">
              <option value="Atelier communautaire">Atelier communautaire</option>
              <option value="Réparation vélo">Station de réparation de vélo</option>
              <option value="Magasin">Magasin de vélo</option>
            </select>
            {Object.keys(newLieu)
              .filter((key) => key !== "type")
              .map((key) => (
                <input key={key} name={key} placeholder={FIELD_PLACEHOLDERS[key]} value={newLieu[key]} onChange={handleNewChange} className="w-full border p-2 rounded" />
              ))}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-300 rounded">
                Annuler
              </button>
              <button onClick={handleAddLieu} className="px-4 py-2 bg-green-600 text-white rounded">
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageCarteVelos;
